import { useCallback, useEffect, useRef, useState } from "react";

interface SidebarStateOptions {
  activeParentLabel: string | null;
  currentPath: string;
  debounceMs?: number;
}

export function useSidebarState({
  activeParentLabel,
  currentPath,
  debounceMs = 120,
}: SidebarStateOptions) {
  const [expandedItems, setExpandedItems] = useState<string[]>(
    activeParentLabel ? [activeParentLabel] : []
  );
  const [isNavPinnedByUser, setIsNavPinnedByUser] = useState(false);
  const lastPathRef = useRef<string | null>(null);
  const lastActiveParentRef = useRef<string | null>(activeParentLabel);
  const autoExpandTimeoutRef = useRef<number | null>(null);
  const manualOverrideRef = useRef(false);

  const clearAutoExpandTimeout = useCallback(() => {
    if (autoExpandTimeoutRef.current) {
      window.clearTimeout(autoExpandTimeoutRef.current);
      autoExpandTimeoutRef.current = null;
    }
  }, []);

  const toggleExpand = useCallback(
    (label: string) => {
      clearAutoExpandTimeout();
      setExpandedItems((prev) => {
        const isClosing = prev[0] === label;
        manualOverrideRef.current = !isClosing;
        setIsNavPinnedByUser(!isClosing);
        return isClosing ? [] : [label];
      });
    },
    [clearAutoExpandTimeout]
  );

  useEffect(() => {
    if (!activeParentLabel) return;
    if (lastPathRef.current === currentPath) return;

    const prevPath = lastPathRef.current;
    lastPathRef.current = currentPath;

    // Reset the manual override ONLY when the active parent section actually
    // changes (e.g. from Dashboards → Finance).  Within-section navigation
    // (e.g. Tasks → Projects inside Operations) should keep the pin intact.
    const parentChanged = activeParentLabel !== lastActiveParentRef.current;
    lastActiveParentRef.current = activeParentLabel;

    if (isNavPinnedByUser && parentChanged) {
      setIsNavPinnedByUser(false);
      manualOverrideRef.current = false;
    }

    const shouldSkipAutoExpand = isNavPinnedByUser && !parentChanged;
    if (shouldSkipAutoExpand) return;
    clearAutoExpandTimeout();

    autoExpandTimeoutRef.current = window.setTimeout(() => {
      if (manualOverrideRef.current) {
        autoExpandTimeoutRef.current = null;
        return;
      }
      setExpandedItems((prev) => (prev[0] === activeParentLabel ? prev : [activeParentLabel]));
      autoExpandTimeoutRef.current = null;
    }, debounceMs);

    return () => {
      clearAutoExpandTimeout();
    };
  }, [activeParentLabel, clearAutoExpandTimeout, currentPath, debounceMs, isNavPinnedByUser]);

  return {
    expandedItems,
    toggleExpand,
    isNavPinnedByUser,
  };
}
